import { gql, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  ArtistPreSaleLotteryApplicantDetailFragmentDoc,
  ArtistPreSaleLotteryApplicantsConnectionFragmentDoc,
  ArtistUpdatePreSaleLotteryApplicantStatusMutation,
  ArtistUpdatePreSaleLotteryApplicantStatusMutationVariables,
  ArtistUpdatePreSaleMutation,
  ArtistUpdatePreSaleMutationVariables,
  ConsoleLotteryManagementsQuery,
  ConsoleLotteryManagementsQueryVariables,
  PreSaleItemFilterFragmentDoc,
  PreSaleLottery,
  PreSaleLotteryApplicantFilterInputs,
  PreSaleStatus,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { dayjs } from '@/lib/dayjs'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { showError, showMessage } = useMessage()
  const { t } = useTranslation('consoleLotteryManagements')
  const { router } = useRouter<{ preSaleId: string }>()
  const [filters, setFilters] = useState<PreSaleLotteryApplicantFilterInputs>(
    {}
  )

  const { data, loading, fetchMore, refetch } = useQuery<
    ConsoleLotteryManagementsQuery,
    ConsoleLotteryManagementsQueryVariables
  >(QUERY, {
    variables: {
      preSaleId: router.query.preSaleId,
      artistPreSaleId: router.query.preSaleId,
      filters: {},
    },
    notifyOnNetworkStatusChange: true,
    returnPartialData: true,
  })

  const [
    artistUpdatePreSaleLotteryApplicantStatus,
    { loading: updatingPreSaleLotteryApplicantStatus },
  ] = useMutation<
    ArtistUpdatePreSaleLotteryApplicantStatusMutation,
    ArtistUpdatePreSaleLotteryApplicantStatusMutationVariables
  >(ARTIST_UPDATE_PRESALE_LOTTERY_APPLICANT_STATUS)

  const [updatePreSaleLottery, { loading: updatingResultNoticedAt }] =
    useMutation<
      ArtistUpdatePreSaleMutation,
      ArtistUpdatePreSaleMutationVariables
    >(UPDATE_PRESALE_LOTTERY_RESULT_NOTICED_AT)

  return {
    state: {
      loading,
      updatingResultNoticedAt,
      hasNextPage: !!data?.artistPreSaleLotteryApplicants?.pageInfo.hasNextPage,
      filters,
      updatingPreSaleLotteryApplicantStatus,
      artistId: user?.uid ?? '',
      preSaleId: router.query.preSaleId,
      preSale: data?.artistPreSale ?? null,
      lotteryApplicants: data?.artistPreSaleLotteryApplicants ?? null,
      isApplied:
        data?.artistPreSale?.preSaleStatus === PreSaleStatus.Accepting ||
        data?.artistPreSale?.preSaleStatus === PreSaleStatus.Expired,
      winDate: data?.artistPreSale?.lottery?.resultNoticedAt ?? '',
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            preSaleId: router.query.preSaleId,
            artistPreSaleId: router.query.preSaleId,
            filters,
            pageInput: {
              cursor: data?.artistPreSaleLotteryApplicants?.pageInfo.endCursor,
            },
          },
        })
      },
      async onFilters(newFilters) {
        setFilters({ ...filters, ...newFilters })
        await refetch({ filters: { ...filters, ...newFilters } })
      },
      async onSelectedWinner(items) {
        if (!data?.artistPreSaleLotteryApplicants.edges.length) {
          return
        }
        await artistUpdatePreSaleLotteryApplicantStatus({
          variables: {
            inputs: {
              preSaleId: router.query.preSaleId,
              lotteryResults: data?.artistPreSaleLotteryApplicants.edges.map(
                ({ node }) => {
                  const item = items.find(i => {
                    const [userId] = i.split(':')
                    return userId === node.id
                  })
                  const [_, winChoiceNumber] = item?.split(':') ?? []
                  return {
                    userId: node.id,
                    winChoiceNumber: +winChoiceNumber || null,
                  }
                }
              ),
            },
          },
        }).catch(showError)
      },
      async onRegisterWinDate(resultNoticedAt: string) {
        if (!data?.artistPreSale) {
          return
        }

        const {
          // @ts-ignore
          __typename,
          id,
          items,
          preSaleStatus,
          type,
          ...presale
        } = data.artistPreSale

        const {
          // @ts-ignore
          __typename: _,
          formattedResultNoticedAt,
          resultNoticeStatus,
          ...lottery
        } = data.artistPreSale.lottery as PreSaleLottery

        await updatePreSaleLottery({
          variables: {
            id: router.query.preSaleId,
            inputs: {
              ...presale,
              lottery: {
                ...lottery,
                resultNoticedAt: dayjs(resultNoticedAt, 'YYYY/MM/DD HH:mm'),
              },
            },
          },
        })
          .then(() => {
            showMessage(t('resultNoticedAtUpdated'))
          })
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query ConsoleLotteryManagements(
    $filters: PreSaleLotteryApplicantFilterInputs
    $pageInput: PageInput
    $artistPreSaleId: String!
    $preSaleId: ID!
  ) {
    artistPreSaleLotteryApplicants(
      filters: $filters
      pageInput: $pageInput
      preSaleId: $artistPreSaleId
    ) {
      ...ArtistPreSaleLotteryApplicantsConnection
    }
    artistPreSale(id: $preSaleId) {
      id
      title
      description
      type
      preSaleStatus @client
      eventId
      lottery {
        applicationStartedAt
        applicationEndedAt
        purchasableStartedAt
        purchasableEndedAt
        resultNoticedAt
        resultNoticeStatus
        maxChoice
        formattedResultNoticedAt @client
        resultNoticeStatus
      }
      items {
        ...PreSaleItemFilter
      }
      itemIds
      isPublished
      endedAt
      startedAt
      note
      paymentMethods
    }
  }
  ${ArtistPreSaleLotteryApplicantsConnectionFragmentDoc}
  ${PreSaleItemFilterFragmentDoc}
`

const ARTIST_UPDATE_PRESALE_LOTTERY_APPLICANT_STATUS = gql`
  mutation ArtistUpdatePreSaleLotteryApplicantStatus(
    $inputs: ArtistUpdatePreSaleLotteryApplicantStatusInput!
  ) {
    artistUpdatePreSaleLotteryApplicantStatus(inputs: $inputs) {
      ...ArtistPreSaleLotteryApplicantDetail
    }
  }
  ${ArtistPreSaleLotteryApplicantDetailFragmentDoc}
`

const UPDATE_PRESALE_LOTTERY_RESULT_NOTICED_AT = gql`
  mutation ArtistUpdatePreSaleLotteryResultNoticedAt(
    $id: ID!
    $inputs: ArtistUpdatePreSaleInput!
  ) {
    artistUpdatePreSale(id: $id, inputs: $inputs) {
      id
      isPublished
      lottery {
        resultNoticedAt
      }
    }
  }
`
